import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { ConversationsPanel } from "@/components/ConversationsPanel";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Building2, Loader2, Check, X, Inbox, MapPin, Clock, FileText } from "lucide-react";

export default function Meddelanden() {
  const [requests, setRequests] = useState(null);
  const [busy, setBusy] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const load = () => api.get("/contact-requests").then((r) => setRequests(r.data)).catch(() => setRequests([]));
  useEffect(() => { load(); }, []);

  const respond = async (id, action) => {
    setBusy(id + action);
    try {
      await api.put(`/contact-requests/${id}`, { action });
      toast.success(action === "accept" ? "Kontakt godkänd – konversation öppnad" : "Förfrågan avböjd");
      load();
      if (action === "accept") setRefreshKey((k) => k + 1);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const pending = (requests || []).filter((r) => r.status === "pending");

  return (
    <div>
      <PageHeader title="Meddelanden" description="Här ser du företag som vill komma i kontakt med dig. De ser bara ditt HAMN-id – aldrig ditt namn, din ålder eller ditt kön. Du väljer själv om du vill godkänna och vad du delar." />

      {pending.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground mb-3"><Inbox className="h-4 w-4" /> Nya kontaktförfrågningar</div>
          <div className="grid gap-3" data-testid="pending-requests">
            {pending.map((r) => (
              <Card key={r.id} className="rounded-2xl border-primary/25 p-5" data-testid={`request-${r.id}`}>
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0"><Building2 className="h-5 w-5" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm">{r.company_name || "Ett företag"}{r.role_title ? ` · ${r.role_title}` : ""}</div>
                    {(r.location || r.extent) && (
                      <div className="flex flex-wrap gap-2 mt-1.5">
                        {r.location && <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {r.location}</span>}
                        {r.extent && <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"><Clock className="h-3 w-3" /> {r.extent}</span>}
                      </div>
                    )}
                    {r.role_description && (
                      <div className="mt-2 rounded-xl border border-border bg-muted/30 p-3" data-testid={`ad-${r.id}`}>
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground mb-1"><FileText className="h-3.5 w-3.5 text-primary" /> Om tjänsten</div>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{r.role_description}</p>
                      </div>
                    )}
                    {r.message && (
                      <div className="mt-2">
                        <div className="text-xs font-semibold text-foreground mb-0.5">Meddelande från företaget</div>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{r.message}</p>
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">Du väljer själv om du vill inleda en konversation.</p>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" className="rounded-full" onClick={() => respond(r.id, "accept")} disabled={busy.startsWith(r.id)} data-testid={`accept-${r.id}`}>
                        {busy === r.id + "accept" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />} Godkänn
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => respond(r.id, "decline")} disabled={busy.startsWith(r.id)} data-testid={`decline-${r.id}`}>
                        <X className="h-4 w-4 mr-1.5" /> Avböj
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground mb-3">Konversationer</div>
      <ConversationsPanel key={refreshKey} canShareContact emptyText="Inga konversationer ännu. När du godkänner en kontaktförfrågan öppnas en konversation här." />
    </div>
  );
}
